import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { User, sendEmailVerification } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import Icon from "./Icon";

// Who Firebase's verification and password emails come from. They often
// land in spam, so we tell people what to look for.
export const SENDER = "noreply@penpalboard.firebaseapp.com";

// Posting, requests, letters, and kudos need a verified email (the rules
// check it), so unverified members get this reminder with a resend button.
// Ask Firebase for this account's latest status. The fresh sign-in token is
// what the database rules check, so it's the source of truth.
async function checkVerified(): Promise<boolean> {
  const current = auth.currentUser;
  if (!current) return false;
  await current.reload();
  const token = await current.getIdTokenResult(true);
  return current.emailVerified || token.claims.email_verified === true;
}

export function VerifyEmailNotice({ user }: { user: User }) {
  const [verified, setVerified] = useState(user.emailVerified);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Coming back from the email link in another tab: check again.
  useEffect(() => {
    if (verified) return;
    const recheck = () =>
      checkVerified()
        .then((ok) => ok && setVerified(true))
        .catch(() => {});
    recheck();
    window.addEventListener("focus", recheck);
    return () => window.removeEventListener("focus", recheck);
  }, [user, verified]);

  if (verified) return null;

  const resend = async () => {
    setBusy(true);
    try {
      await sendEmailVerification(user);
      setNote(`Sent! Check ${user.email} (and your spam folder).`);
    } catch (err: any) {
      setNote(
        err?.code === "auth/too-many-requests"
          ? "We just sent one. Please wait a few minutes before asking again."
          : "Sorry, that didn't send. Please try again."
      );
    }
    setBusy(false);
  };

  const done = async () => {
    setBusy(true);
    setNote("");
    try {
      if (await checkVerified()) setVerified(true);
      else setNote("Not verified yet. Click the link in the newest email first.");
    } catch (err) {
      console.error("Couldn't check verification:", err);
      setNote("We couldn't check just now. Try logging out and signing back in.");
    }
    setBusy(false);
  };

  return (
    <div className="notice mx-auto mb-3 flex max-w-5xl flex-wrap items-center gap-2 text-sm" role="status">
      <Icon name="mail" size={16} />
      <span className="min-w-0 flex-1">
        <strong>Please verify your email</strong> to post, send requests and
        kudos, and write letters. The link went to {user.email} from{" "}
        {SENDER}. <strong>Check your spam folder</strong> and mark it "Not spam".
        {note && <span className="block font-semibold">{note}</span>}
      </span>
      <button type="button" className="btn btn-sm" onClick={done} disabled={busy}>
        I've verified
      </button>
      <button type="button" className="btn btn-plain btn-sm" onClick={resend} disabled={busy}>
        Resend email
      </button>
    </div>
  );
}

// Shown to someone an admin has suspended (a doc at /suspended/{uid}).
export function SuspendedNotice({ uid }: { uid: string }) {
  const [suspended, setSuspended] = useState(false);
  useEffect(() => {
    getDoc(doc(db, "suspended", uid))
      .then((snap) => setSuspended(snap.exists()))
      .catch(() => setSuspended(false));
  }, [uid]);
  if (!suspended) return null;
  return (
    <div className="notice mx-auto mb-3 max-w-5xl text-sm" role="alert">
      <strong>Your account is suspended.</strong> You can still read, but you
      can't post, send requests or kudos, write letters, or change your
      profile. If you think this is a mistake, email{" "}
      <a href="mailto:2slgbtqiapenpal@gmail.com">2slgbtqiapenpal@gmail.com</a>.
      You can also read the <Link to="/rules">community guidelines</Link>.
    </div>
  );
}
